import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { listingPath } from "@/lib/constants";
import { LoadFailed } from "@/components/LoadState";
import { toast } from "@/lib/toast";
import {
  ShoppingCart, Truck, CheckCircle2, AlertTriangle, Ban, Shirt, Loader2,
  MailWarning, Download, MessagesSquare, MessageSquare, Undo2, ChevronDown, Clock, Bookmark, Rows3, Users2, Pin, RefreshCw,
} from "lucide-react";
import { RoleTag } from "@/components/admin/RoleTag";
import { AdminDmViewer } from "@/components/admin/AdminDmViewer";
import { DiscussButton } from "@/components/admin/DiscussButton";
import { useAuth } from "@/context/AuthContext";
import "./AdminOrders.css";

const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];
const fmtDate = (iso) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };
const dayOf = (iso) => (iso ? String(iso).slice(0, 10) : "");
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }) + " UTC";
};

// Canonical filter/display status for BOTH escrow and swag orders in this one table.
function fstatusOf(o) {
  if (o.type === "swag") return o.status === "completed" ? "paid_to_ship" : o.status;
  if (o.status === "shipped" && o.needs_tracking_review) return "tracking_review";
  if (o.needs_address_review) return "address_review";
  return o.status;
}
const STATUS_META = {
  paid_to_ship: { label: "Paid — To Ship", cls: "st-paid" },
  partially_shipped: { label: "Partially Shipped", cls: "st-partial" },
  held: { label: "Held", cls: "st-held" },
  shipped: { label: "Shipped", cls: "st-shipped" },
  tracking_review: { label: "Tracking Review", cls: "st-review" },
  address_review: { label: "Address Review", cls: "st-review" },
  disputed: { label: "Disputed", cls: "st-disputed" },
  delivery_exception: { label: "Delivery Exception", cls: "st-exception" },
  payout_held: { label: "Payout Held", cls: "st-payout" },
  release_pending: { label: "Release Pending", cls: "st-releasepending" },
  released: { label: "Released", cls: "st-released" },
  refunded: { label: "Refunded", cls: "st-refunded" },
  cancelled: { label: "Cancelled", cls: "st-refunded" },
};
const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "awaiting_shipment", label: "Awaiting Shipment" },
  { value: "paid_to_ship", label: "Paid — To Ship" },
  { value: "held", label: "Held" },
  { value: "shipped", label: "Shipped" },
  { value: "tracking_review", label: "Tracking Review" },
  { value: "address_review", label: "Address Review" },
  { value: "disputed", label: "Disputed" },
  { value: "delivery_exception", label: "Delivery Exception" },
  { value: "payout_held", label: "Payout Held" },
  { value: "release_pending", label: "Release Pending" },
  { value: "released", label: "Released" },
  { value: "refunded", label: "Refunded" },
];
function statusMatches(o, val) {
  if (!val) return true;
  if (val === "awaiting_shipment") return (o.type === "escrow" && o.status === "held") || (o.type === "swag" && o.status === "completed");
  return fstatusOf(o) === val;
}

// Deep-links from the dashboard/sidebar (?status=…) map onto the new filter model.
const DEEPLINK = { awaiting: ["", "awaiting_shipment"], swag: ["swag", ""], all: ["", ""] };
function deeplinkFilters(s) {
  if (!s || s === "all") return { type: "", status: "" };
  if (DEEPLINK[s]) return { type: DEEPLINK[s][0], status: DEEPLINK[s][1] };
  return { type: "", status: STATUS_OPTIONS.some((x) => x.value === s) ? s : "" };
}

const custName = (o) => o.type === "swag"
  ? (o.shipping?.name || (o.username ? `@${o.username}` : "—"))
  : (o.buyer_username ? `@${o.buyer_username}` : (o.buyer_id || "—"));
const custMail = (o) => o.type === "swag"
  ? (o.email || o.shipping?.email || "")
  : (o.seller_username ? `→ @${o.seller_username}` : (o.seller_id ? `→ ${o.seller_id}` : ""));
const custSearch = (o) => [o.buyer_username, o.seller_username, o.username, o.email, o.shipping?.name, o.shipping?.email, o.buyer_id, o.seller_id].filter(Boolean).join(" ").toLowerCase();

// Human labels for the carrier tracking layer (informational; separate from the money status).
const TRACK_LABEL = {
  pre_transit: "Label created", in_transit: "In transit", out_for_delivery: "Out for delivery",
  available_for_pickup: "Held at facility — awaiting pickup", delivered: "Delivered",
  return_to_sender: "Return to sender", failure: "Delivery failed", error: "Carrier error",
  cancelled: "Tracking cancelled", unknown: "Status unknown",
};
const EXC_LABEL = {
  return_to_sender: "Package is being returned to the sender — funds cannot release.",
  failure: "Carrier reported delivery failure — funds cannot release.",
  error: "Carrier reported a tracking error — funds cannot release.",
  cancelled: "Tracking was cancelled — funds cannot release.",
};
const PAYOUT_HOLD_COPY = {
  seller_revoked: "Payout held — seller ID revoked. The money is EARNED (buyer approved, gear arrived): release once you're satisfied, or refund. Not a freeze.",
  reconnect_required: "Payout held — the seller's Stripe payout account is orphaned (unreachable with our key). No transfer was attempted. The seller must reconnect payouts before this can release.",
  payout_unverifiable: "Payout held — we could not verify the seller's payout account with Stripe just now, so no transfer was attempted. Retry once Stripe is reachable; the money is safe.",
  payout_not_ready: "Payout held — the seller's Stripe account is reachable but not payout-ready (requirements due / restricted). No transfer was attempted until it's enabled.",
};

const heldDays = (o) => {
  const iso = o.payout_held_at || o.created_at;
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d < 0 ? 0 : d;
};
const holdTierCls = (d) => (d >= 7 ? "t3" : d >= 3 ? "t2" : d >= 1 ? "t1" : "t0");
const holdLabel = (d) => (d === 0 ? "Held today" : d === 1 ? "Held 1 day" : `Held ${d} days`);
function HoldAgeBadge({ order }) {
  const d = heldDays(order);
  if (d == null) return null;
  return (
    <span data-testid={`order-hold-age-${order.order_id}`} title={`On hold since ${dayOf(order.payout_held_at || order.created_at)}`}
      className={`hold-badge ${holdTierCls(d)}`}>
      {d >= 7 && <AlertTriangle className="w-3 h-3" />}{holdLabel(d)}
    </span>
  );
}

// ---- Swag fulfillment line: its own carrier/tracking, shipped independently. ----
function SwagLineRow({ order, line, onChanged }) {
  const [carrier, setCarrier] = useState(line.carrier || "USPS");
  const [tracking, setTracking] = useState(line.tracking_number || "");
  const [busy, setBusy] = useState(false);
  const variant = [line.color, line.size].filter(Boolean).join(" · ");
  const shipped = !!line.shipped;

  const markShipped = async () => {
    if (!tracking.trim()) { toast.error("Enter a tracking number."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/swag/orders/${order.order_id}/ship-line`, { line_id: line.line_id, carrier, tracking_number: tracking.trim() });
      toast.success("Line marked shipped — buyer emailed the tracking details.");
      onChanged();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 10 }} data-testid={`swag-line-${order.order_id}-${line.line_id}`}>
      <div className="itemrow" style={{ borderBottom: "none", paddingBottom: 2 }}>
        <span>{line.quantity}× {line.item_name}{variant && <b data-testid={`swag-line-variant-${line.line_id}`}> {variant}</b>}
          {line.preorder && <b style={{ color: "var(--ord-gold)" }}> · Pre-order</b>}
        </span>
        <span className="px" data-testid={`swag-line-status-${line.line_id}`} style={{ color: shipped ? "var(--ord-steel)" : "var(--ord-gold)" }}>
          {shipped ? "Shipped" : "To ship"}
        </span>
      </div>
      {shipped ? (
        (line.carrier || line.tracking_number) && (
          <div className="line" data-testid={`swag-line-tracking-${line.line_id}`} style={{ color: "var(--ord-steel)", display: "flex", alignItems: "center", gap: 6 }}>
            <Truck className="w-3.5 h-3.5" /> {line.carrier ? `${line.carrier} · ` : ""}{line.tracking_number}
          </div>
        )
      ) : (
        <div className="trackrow">
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`swag-line-carrier-${line.line_id}`}>
            {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="1Z…" data-testid={`swag-line-tracking-input-${line.line_id}`} />
          <button className="btn-ship" onClick={markShipped} disabled={busy} data-testid={`swag-line-ship-btn-${line.line_id}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Truck className="w-3.5 h-3.5" />} Mark shipped
          </button>
        </div>
      )}
    </div>
  );
}

// ---- Swag order expanded detail (ship-to / fulfillment / contents). ----
function SwagDetail({ order, onChanged }) {
  const ship = order.shipping || {};
  const items = order.items && order.items.length ? order.items : [{ line_id: "_only", item_name: order.item_name, quantity: order.quantity || 1, amount_cents: order.amount_cents, color: order.color, size: order.size, shipped: order.status === "shipped", carrier: order.carrier, tracking_number: order.tracking_number, preorder: order.preorder }];
  const emailFailed = order.fulfillment_email_ok === false;
  const subtotal = items.reduce((s, l) => s + (l.amount_cents || 0), 0);
  const grand = order.amount_cents || subtotal;
  const shippingCents = Math.max(0, grand - subtotal);

  return (
    <div className="detailgrid">
      <div className="dcol">
        <h4>Ship to</h4>
        <div className="line">
          <b>{ship.name || order.username || "—"}</b><br />
          {ship.address1}{ship.address2 ? <>, {ship.address2}</> : null}
          {ship.city ? <><br />{ship.city}, {ship.state} {ship.zip}</> : null}
          {ship.email ? <><br /><span className="muted">{ship.email}</span></> : null}
          <br /><span className="muted">@{order.username || "—"} · {order.order_id}</span>
        </div>
        {emailFailed && (
          <div className="alert err" data-testid={`swag-email-failed-${order.order_id}`}>
            <MailWarning className="w-3.5 h-3.5" /> Fulfillment email did NOT send{order.fulfillment_email_error ? ` — ${order.fulfillment_email_error}` : ""}. Check Resend / SITREP email failures.
          </div>
        )}
      </div>
      <div className="dcol">
        <h4>Fulfillment</h4>
        <div data-testid={`swag-lines-${order.order_id}`}>
          <div className="line muted">{items.length} line{items.length === 1 ? "" : "s"}</div>
          {items.map((l) => <SwagLineRow key={l.line_id} order={order} line={l} onChanged={onChanged} />)}
        </div>
      </div>
      <div className="dcol">
        <h4>Order contents</h4>
        {items.map((l) => (
          <div className="itemrow" key={`c-${l.line_id}`}>
            <span>{l.quantity}× {l.item_name} {[l.color, l.size].filter(Boolean).length ? <b>{[l.color, l.size].filter(Boolean).join(" · ")}</b> : null}</span>
            <span className="px">{money(l.amount_cents)}</span>
          </div>
        ))}
        <div className="totals">
          <div className="tr2"><span>Subtotal</span><span>{money(subtotal)}</span></div>
          <div className="tr2"><span>Shipping</span><span>{money(shippingCents)}</span></div>
          <div className="tr2 grand"><span>Grand total</span><span>{money(grand)}</span></div>
        </div>
      </div>
    </div>
  );
}

// ---- Escrow order expanded detail (parties / shipping+tracking / settlement + actions). ----
function EscrowDetail({ order: o, busy, resolve, addressReview, simulateDelivery, openDm }) {
  const actionable = ["held", "shipped", "disputed", "release_pending", "payout_held", "delivery_exception"].includes(o.status);
  const dmAccessible = o.status === "disputed" || o.chargeback_open;
  const ar = o.address_review;
  return (
    <div className="detailgrid">
      <div className="dcol">
        <h4>Parties</h4>
        <div className="line">
          <Link className="link" to={listingPath(o)}><b>{o.listing_title || "Listing"}</b></Link><br />
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 4 }}>
            <Link className="link" to={`/u/${o.buyer_username}`}>@{o.buyer_username || o.buyer_id}</Link><RoleTag kind={o.buyer_kind} />
            <span className="muted">→</span>
            <Link className="link" to={`/u/${o.seller_username}`}>@{o.seller_username || o.seller_id}</Link><RoleTag kind={o.seller_kind} />
          </span>
          <br /><span className="muted">{o.order_id}</span>
        </div>
        {o.conflict_flag && (
          <div className="alert err" data-testid={`order-conflict-${o.order_id}`}>
            <Ban className="w-3.5 h-3.5" /> Conflict: @{o.conflict_flag.blocker_username} blocked @{o.conflict_flag.blocked_username} ({o.conflict_flag.reason}) — watch this order.
          </div>
        )}
      </div>

      <div className="dcol">
        <h4>Shipping &amp; tracking</h4>
        {o.tracking_number ? (
          <div className="line" style={{ color: "var(--ord-steel)", display: "flex", alignItems: "center", gap: 6 }}>
            <Truck className="w-3.5 h-3.5" /> {o.carrier} · {o.tracking_number}
          </div>
        ) : <div className="line muted">No shipment recorded yet.</div>}
        {o.tracking_status && (
          <div className="line" data-testid={`order-tracking-${o.order_id}`}>
            <span style={{ color: o.tracking_status === "delivered" ? "var(--ord-lime)" : o.tracking_status === "available_for_pickup" ? "var(--ord-gold)" : "var(--ord-dim)" }}>
              {TRACK_LABEL[o.tracking_status] || o.tracking_status}
            </span>
            {o.delivery_signed_by && <span style={{ color: "var(--ord-lime)" }}> · signed by {o.delivery_signed_by}</span>}
            {o.delivered_at && o.tracking_status === "delivered" && <span className="muted"> · {dayOf(o.delivered_at)}</span>}
          </div>
        )}
        {o.delivery_attempted_at && o.status !== "delivery_exception" && (
          <div className="alert warn" data-testid={`order-attempt-${o.order_id}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> Delivery attempted, not completed ({dayOf(o.delivery_attempted_at)}) — clocks still running.
          </div>
        )}
        {o.status === "delivery_exception" && (
          <div className="alert err" data-testid={`order-delivery-exception-${o.order_id}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {EXC_LABEL[o.delivery_exception_reason] || "Delivery problem — funds cannot release."} Release or refund →
          </div>
        )}
        {o.needs_tracking_review && o.status === "shipped" && (
          <div className="alert amber" data-testid={`order-tracking-review-${o.order_id}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {o.tracking_review_reason === "checksum_failed"
              ? "Tracking number failed its carrier check digit — verify it before releasing. Funds will NOT auto-release."
              : o.tracking_review_reason === "no_delivery_scan"
              ? "No carrier delivery scan since shipping — the tracking number may be invalid. Funds will NOT auto-release; verify or refund."
              : "Tracking couldn't be verified with the carrier — no delivery scan will arrive. Funds will NOT auto-release; verify or refund."}
          </div>
        )}
        {ar && (
          <div className="alert warn" data-testid={`order-address-review-${o.order_id}`} style={{ flexDirection: "column" }}>
            <span style={{ fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle className="w-3.5 h-3.5" /> PO Box exception · {ar.status}
            </span>
            <span data-testid={`address-review-addr-${o.order_id}`} style={{ color: "var(--ord-dim)" }}>
              {ar.ship_to?.street1}{ar.ship_to?.street2 ? `, ${ar.ship_to.street2}` : ""} · {ar.ship_to?.city}, {ar.ship_to?.state} {ar.ship_to?.zip}
            </span>
            {ar.reason && <span className="muted">Buyer reason: “{ar.reason}”</span>}
            {ar.reviewed_by && <span className="muted">{ar.status === "approved" ? "Approved" : "Rejected"} by @{ar.reviewed_by}{ar.review_note ? ` · ${ar.review_note}` : ""}</span>}
            {o.needs_address_review && (
              <span className="dactions" style={{ marginTop: 8 }}>
                <button className="dbtn release" disabled={busy === o.order_id} onClick={() => addressReview(o, "approve")} data-testid={`address-review-approve-${o.order_id}`}>
                  <CheckCircle2 className="w-3.5 h-3.5" /> Approve address
                </button>
                <button className="dbtn refund" disabled={busy === o.order_id} onClick={() => addressReview(o, "reject")} data-testid={`address-review-reject-${o.order_id}`}>
                  <Ban className="w-3.5 h-3.5" /> Reject
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      <div className="dcol">
        <h4>Settlement</h4>
        <div className="totals" style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
          <div className="tr2"><span>Order total</span><span>{money(o.amount_cents)}</span></div>
          <div className="tr2"><span>Platform fee ({o.commission_rate}%)</span><span>{money(o.commission_cents)}</span></div>
          <div className="tr2 grand"><span>Seller payout</span><span>{money(o.payout_cents)}</span></div>
        </div>
        {o.status === "payout_held" && <HoldAgeBadge order={o} />}
        {o.status === "payout_held" && (
          <div className="alert warn" data-testid={`order-payout-held-${o.order_id}`}>
            <AlertTriangle className="w-3.5 h-3.5" /> {PAYOUT_HOLD_COPY[o.payout_hold_reason] || "Payout held — review before releasing or refunding."}
          </div>
        )}
        {o.status === "disputed" && o.dispute_reason && (
          <div className="alert err"><AlertTriangle className="w-3.5 h-3.5" /> {o.dispute_reason}</div>
        )}
        {o.chargeback_open && (
          <div className="alert err" data-testid={`order-chargeback-${o.order_id}`} style={{ flexDirection: "column" }}>
            <span style={{ fontWeight: 700, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
              <AlertTriangle className="w-4 h-4" /> Chargeback open · {o.chargeback_reason || "unknown"}
            </span>
            {o.chargeback_due_by && <span>Respond by {String(o.chargeback_due_by).slice(0, 16).replace("T", " ")} UTC · fee applies win or lose · release blocked</span>}
            {o.chargeback_after_release && <span style={{ fontWeight: 700 }}>Funds were ALREADY RELEASED to the seller — platform is exposed.</span>}
          </div>
        )}
        {o.release_error && <div className="alert err"><AlertTriangle className="w-3.5 h-3.5" /> Release error: {o.release_error}</div>}

        <div className="dactions" data-testid={`order-tools-${o.order_id}`}>
          {actionable && (
            <>
              <button className="dbtn release" disabled={busy === o.order_id || o.chargeback_open} onClick={() => resolve(o, "release")} data-testid={`resolve-release-${o.order_id}`}
                title={o.chargeback_open ? "Release is blocked while a chargeback is open" : ""}>
                <CheckCircle2 className="w-4 h-4" /> Release to seller
              </button>
              <button className="dbtn refund" disabled={busy === o.order_id} onClick={() => resolve(o, "refund")} data-testid={`resolve-refund-${o.order_id}`}>
                <RefreshCw className="w-4 h-4" /> Refund buyer
              </button>
            </>
          )}
          {["held", "shipped"].includes(o.status) && !o.chargeback_open && (
            <button className="dbtn sim" disabled={busy === o.order_id} onClick={() => simulateDelivery(o)} data-testid={`simulate-delivery-${o.order_id}`}
              title="Test tool — simulate a carrier delivered scan (no physical parcel)">
              <Truck className="w-3.5 h-3.5" /> Simulate delivered
            </button>
          )}
          {dmAccessible && (
            <button className="dbtn ghost" onClick={() => openDm(o)} data-testid={`view-dm-${o.order_id}`}>
              <MessageSquare className="w-3.5 h-3.5" /> View messages
            </button>
          )}
          <DiscussButton type={dmAccessible ? "dispute" : "order"} id={o.order_id} />
        </div>
      </div>
    </div>
  );
}

// ---- Per-row "…" action menu (consolidates the existing order actions). ----
function RowMenu({ order: o, onClose, expand, resolve, refund, simulate, openDm, discuss }) {
  const actionable = ["held", "shipped", "disputed", "release_pending", "payout_held", "delivery_exception"].includes(o.status);
  const dmAccessible = o.status === "disputed" || o.chargeback_open;
  const act = (fn) => (e) => { e.stopPropagation(); onClose(); fn(); };
  return (
    <div className="ord-menu" data-testid={`order-menu-${o.order_id}`} onClick={(e) => e.stopPropagation()}>
      <button onClick={act(expand)} data-testid={`order-menu-view-${o.order_id}`}><ChevronDown /> View / edit details</button>
      {o.type === "escrow" && actionable && (
        <button onClick={act(() => resolve(o, "release"))} disabled={o.chargeback_open} data-testid={`order-menu-release-${o.order_id}`}><CheckCircle2 /> Release to seller</button>
      )}
      {o.type === "escrow" && ["held", "shipped"].includes(o.status) && !o.chargeback_open && (
        <button onClick={act(() => simulate(o))} data-testid={`order-menu-simulate-${o.order_id}`}><Truck /> Simulate delivered</button>
      )}
      {o.type === "escrow" && dmAccessible && (
        <button onClick={act(() => openDm(o))} data-testid={`order-menu-dm-${o.order_id}`}><MessagesSquare /> View messages</button>
      )}
      <button onClick={act(() => discuss(o))} data-testid={`order-menu-discuss-${o.order_id}`}><MessagesSquare /> Discuss with team</button>
      {o.type === "escrow" && actionable && (
        <button className="danger" onClick={act(() => refund(o))} data-testid={`order-menu-refund-${o.order_id}`}><Undo2 /> Refund buyer</button>
      )}
    </div>
  );
}

// ---- Chronological "what happened & who acted" trail for one order (dispute triage). ----
function OrderTimeline({ orderId }) {
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    setEvents(null); setErr(null);
    api.get(`/admin/orders/timeline/${orderId}`)
      .then(({ data }) => { if (live) setEvents(data.events || []); })
      .catch((e) => { if (live) setErr(formatError(e.response?.data?.detail) || "Couldn't load timeline."); });
    return () => { live = false; };
  }, [orderId, reloadKey]);

  const addNote = async () => {
    const t = note.trim();
    if (!t) return;
    setSaving(true);
    try {
      await api.post(`/admin/orders/timeline/${orderId}/note`, { text: t });
      setNote("");
      setReloadKey((k) => k + 1);
      toast.success("Note pinned to the timeline.");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="tl-notebox" data-testid={`timeline-note-box-${orderId}`}>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          placeholder="Pin a note — e.g. called buyer, awaiting reply…"
          onKeyDown={(e) => { if (e.key === "Enter") addNote(); }}
          data-testid={`timeline-note-input-${orderId}`} />
        <button onClick={addNote} disabled={saving || !note.trim()} data-testid={`timeline-note-add-${orderId}`}>
          <Pin /> {saving ? "Adding…" : "Add note"}
        </button>
      </div>
      {err ? (
        <div className="tl-empty" data-testid={`order-timeline-error-${orderId}`}>{err}</div>
      ) : !events ? (
        <div className="tl-empty">Loading timeline…</div>
      ) : !events.length ? (
        <div className="tl-empty" data-testid={`order-timeline-empty-${orderId}`}>No timeline events yet.</div>
      ) : (
        <ol className="timeline" data-testid={`order-timeline-${orderId}`}>
          {events.map((e, i) => (
            <li key={i} className={`tl-item tl-${e.kind}`} data-testid={`timeline-event-${orderId}-${i}`}>
              <span className="tl-dot" />
              <div className="tl-body">
                <div className="tl-label">{e.label}{e.actor && <span className="tl-actor"> · by @{e.actor}</span>}</div>
                {e.detail && <div className="tl-detail">{e.detail}</div>}
                <div className="tl-ts">{fmtDateTime(e.ts)}</div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---- Row-expand shell: Details / Timeline tabs over the per-type detail. ----
function OrderExpand({ order, busy, resolve, addressReview, simulateDelivery, openDm, onChanged }) {
  const [tab, setTab] = useState("details");
  return (
    <div>
      <div className="exp-tabs" data-testid={`order-exp-tabs-${order.order_id}`}>
        <button className={tab === "details" ? "active" : ""} onClick={() => setTab("details")} data-testid={`exp-tab-details-${order.order_id}`}>Details</button>
        <button className={tab === "timeline" ? "active" : ""} onClick={() => setTab("timeline")} data-testid={`exp-tab-timeline-${order.order_id}`}><Clock /> Timeline</button>
      </div>
      {tab === "details" ? (
        order.type === "swag"
          ? <SwagDetail order={order} onChanged={onChanged} />
          : <EscrowDetail order={order} busy={busy} resolve={resolve} addressReview={addressReview} simulateDelivery={simulateDelivery} openDm={openDm} />
      ) : (
        <OrderTimeline orderId={order.order_id} />
      )}
    </div>
  );
}

export default function AdminOrders() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loadErr, setLoadErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(null);
  const [dmOrder, setDmOrder] = useState(null);
  const [busy, setBusy] = useState("");

  const initDeep = deeplinkFilters(searchParams.get("status"));
  const [fType, setFType] = useState(initDeep.type);
  const [fStatus, setFStatus] = useState(initDeep.status);
  const [fOid, setFOid] = useState("");
  const [fCust, setFCust] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const [sortKey, setSortKey] = useState("date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const [expanded, setExpanded] = useState(() => new Set());
  const [menuFor, setMenuFor] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem("os_orders_views") || "[]"); } catch { return []; } });
  const [sharedViews, setSharedViews] = useState([]);
  const [density, setDensity] = useState(() => localStorage.getItem("os_orders_density") || "comfortable");
  const [bulkShipOpen, setBulkShipOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkCarrier, setBulkCarrier] = useState("USPS");
  const [bulkTracking, setBulkTracking] = useState("");
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState("");
  const [saveViewShare, setSaveViewShare] = useState(false);

  const load = () => {
    setLoadErr(null);
    setLoading(true);
    const escrowP = api.get("/admin/orders").then(({ data }) => (data.orders || []).map((o) => ({ ...o, type: "escrow" }))).catch(() => { throw new Error("escrow"); });
    const swagP = api.get("/admin/swag/orders").then(({ data }) => (data.orders || []).map((o) => ({ ...o, type: "swag" }))).catch(() => []);
    Promise.all([escrowP, swagP])
      .then(([escrow, swag]) => setOrders([...escrow, ...swag]))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load orders."))
      .finally(() => setLoading(false));
    api.get("/admin/orders/counts").then(({ data }) => setCounts(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const loadSharedViews = () => api.get("/admin/orders/saved-views")
    .then(({ data }) => setSharedViews((data.views || []).map((v) => ({ ...v, shared: true }))))
    .catch(() => {});
  useEffect(() => { loadSharedViews(); }, []); // eslint-disable-line

  // Deep-link from global search: /admin/orders?order=<id> surfaces + opens that one order.
  useEffect(() => {
    const oid = searchParams.get("order");
    if (oid) { setFOid(oid); setExpanded(new Set([oid])); }
  }, [searchParams]);

  const clearFilters = () => { setFType(""); setFStatus(""); setFOid(""); setFCust(""); setFFrom(""); setFTo(""); setPage(1); };
  const setStatusScope = (s) => { setFStatus(s); setFType(""); setPage(1); };

  const filtered = useMemo(() => {
    const oid = fOid.trim().toLowerCase();
    const cust = fCust.trim().toLowerCase();
    return orders.filter((o) => {
      if (fType && o.type !== fType) return false;
      if (!statusMatches(o, fStatus)) return false;
      if (oid && !String(o.order_id || "").toLowerCase().includes(oid)) return false;
      if (cust && !custSearch(o).includes(cust)) return false;
      const d = dayOf(o.created_at);
      if (fFrom && d && d < fFrom) return false;
      if (fTo && d && d > fTo) return false;
      return true;
    });
  }, [orders, fType, fStatus, fOid, fCust, fFrom, fTo]);

  const sorted = useMemo(() => {
    const val = (o) => {
      switch (sortKey) {
        case "oid": return String(o.order_id || "").toLowerCase();
        case "customer": return custName(o).toLowerCase();
        case "type": return o.type || "";
        case "status": return (STATUS_META[fstatusOf(o)]?.label || "").toLowerCase();
        default: return String(o.created_at || "");
      }
    };
    const arr = [...filtered].sort((a, b) => { const va = val(a), vb = val(b); return va < vb ? -1 : va > vb ? 1 : 0; });
    return sortDir === "desc" ? arr.reverse() : arr;
  }, [filtered, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / perPage));
  const curPage = Math.min(page, totalPages);
  const pageRows = sorted.slice((curPage - 1) * perPage, curPage * perPage);
  const selectedSwagShippable = orders.filter((o) => selected.has(o.order_id) && o.type === "swag" && ["completed", "partially_shipped"].includes(o.status));

  useEffect(() => { setPage(1); }, [fType, fStatus, fOid, fCust, fFrom, fTo, perPage]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir(key === "date" ? "desc" : "asc"); }
  };
  const toggleExpand = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const allPageSelected = pageRows.length > 0 && pageRows.every((o) => selected.has(o.order_id));
  const toggleSelectAll = () => setSelected((prev) => {
    const n = new Set(prev);
    if (allPageSelected) pageRows.forEach((o) => n.delete(o.order_id));
    else pageRows.forEach((o) => n.add(o.order_id));
    return n;
  });

  const resolve = async (o, resolution) => {
    const verb = resolution === "release" ? "release funds to the seller" : "refund the buyer";
    if (!window.confirm(`Are you sure you want to ${verb} for order ${o.order_id}?`)) return;
    setBusy(o.order_id);
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/resolve`, { resolution });
      toast.success(data.status === "released" ? "Released to seller." : data.status === "refunded" ? "Refunded to buyer." : `Order ${data.status}.`);
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const addressReview = async (o, action) => {
    if (!window.confirm(`${action === "approve" ? "Approve" : "Reject"} the PO Box shipping address for order ${o.order_id}?`)) return;
    const note = window.prompt("Add a note (optional):", "") || "";
    setBusy(o.order_id);
    try {
      await api.post(`/admin/orders/${o.order_id}/address-review`, { action, note });
      toast.success(action === "approve" ? "Address approved." : "Address rejected.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  // TEST TOOL — simulate a carrier "delivered" scan so escrow release can be tested without a
  // physical parcel. Flagged `simulated_delivery` server-side so it's never mistaken for a real scan.
  const simulateDelivery = async (o) => {
    if (!window.confirm(`TEST TOOL: simulate a carrier "delivered" scan on order ${o.order_id}? This bypasses a physical parcel and marks it delivered (flagged as simulated).`)) return;
    const releaseNow = window.confirm("Release funds to the seller immediately?\n\nOK = release now  ·  Cancel = let the auto-release loop settle it shortly");
    setBusy(o.order_id);
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/simulate-delivery`, { release_now: releaseNow });
      const st = data.release_result?.status || data.status;
      toast.success(releaseNow ? `Simulated delivery — release result: ${String(st).replace("_", " ")}.` : "Simulated delivery — the auto-release loop will settle it shortly.");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const discuss = async (o) => {
    try {
      const type = o.type === "swag" ? "order" : (o.status === "disputed" || o.chargeback_open ? "dispute" : "order");
      const { data } = await api.post("/admin/team/discuss", { type, id: o.order_id });
      navigate(`/admin/team?channel=${data.channel_id}&thread=${data.thread_root_id}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const exportCsv = () => {
    const rows = selected.size ? sorted.filter((o) => selected.has(o.order_id)) : sorted;
    if (!rows.length) { toast.info("Nothing to export."); return; }
    const head = ["Date", "Order ID", "Type", "Customer", "Email", "Status", "Total"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = rows.map((o) => [dayOf(o.created_at), o.order_id, o.type, custName(o), (o.type === "swag" ? (o.email || o.shipping?.email || "") : ""), STATUS_META[fstatusOf(o)]?.label || o.status, money(o.amount_cents)].map(esc).join(","));
    const csv = [head.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `opticswap-orders-${dayOf(new Date().toISOString())}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} order${rows.length === 1 ? "" : "s"}.`);
  };

  // --- Saved Views: personal (localStorage) + team-shared (backend). ---
  const persistViews = (next) => { setViews(next); try { localStorage.setItem("os_orders_views", JSON.stringify(next)); } catch { /* quota */ } };
  const saveView = () => { setSaveViewName(""); setSaveViewShare(false); setSaveViewOpen(true); };
  const confirmSaveView = async () => {
    const name = saveViewName.trim();
    if (!name) return;
    const payload = { name: name.slice(0, 40), f: { type: fType, status: fStatus, oid: fOid, cust: fCust, from: fFrom, to: fTo }, sort: { key: sortKey, dir: sortDir }, perPage };
    if (saveViewShare) {
      try { await api.post("/admin/orders/saved-views", payload); toast.success(`Team view "${name}" saved.`); loadSharedViews(); }
      catch (err) { toast.error(formatError(err.response?.data?.detail)); return; }
    } else {
      persistViews([...views, { id: `v_${Date.now().toString(36)}`, name: name.slice(0, 40), f: payload.f, sort: payload.sort, perPage }]);
      toast.success(`View "${name}" saved.`);
    }
    setSaveViewOpen(false); setSaveViewName(""); setSaveViewShare(false);
  };
  const applyView = (v) => {
    setFType(v.f?.type || ""); setFStatus(v.f?.status || ""); setFOid(v.f?.oid || ""); setFCust(v.f?.cust || "");
    setFFrom(v.f?.from || ""); setFTo(v.f?.to || "");
    setSortKey(v.sort?.key || "date"); setSortDir(v.sort?.dir || "desc");
    setPerPage(v.perPage || 20); setPage(1);
  };
  const deleteView = async (v) => {
    if (v.shared) {
      try { await api.delete(`/admin/orders/saved-views/${v.id}`); loadSharedViews(); }
      catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    } else {
      persistViews(views.filter((x) => x.id !== v.id));
    }
  };

  const toggleDensity = () => setDensity((d) => { const next = d === "compact" ? "comfortable" : "compact"; try { localStorage.setItem("os_orders_density", next); } catch { /* quota */ } return next; });

  // --- Bulk mark-shipped for the selected swag orders (via modal; tracking optional). ---
  const bulkMarkShipped = () => {
    if (!selectedSwagShippable.length) { toast.info("Select swag orders that are awaiting shipment."); return; }
    setBulkCarrier("USPS"); setBulkTracking(""); setBulkShipOpen(true);
  };
  const confirmBulkShip = async () => {
    const ids = selectedSwagShippable.map((o) => o.order_id);
    if (!ids.length) { setBulkShipOpen(false); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/swag/orders/bulk-ship", { order_ids: ids, carrier: bulkCarrier || "USPS", tracking_number: bulkTracking.trim() || null });
      toast.success(`Marked ${data.count} order(s) shipped${data.skipped?.length ? `, ${data.skipped.length} skipped` : ""}.`);
      setBulkShipOpen(false); setBulkTracking(""); setSelected(new Set()); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  const SortHead = ({ label, k }) => (
    <th className="sortable" onClick={() => toggleSort(k)} data-testid={`orders-sort-${k}`}>
      {label}
      <span className={`sortarrow ${sortKey === k ? "" : "idle"}`}>{sortKey === k ? (sortDir === "asc" ? "▴" : "▾") : "▾"}</span>
    </th>
  );

  const needsAction = counts?.needs_action || 0;

  return (
    <div className={`orders-rd${density === "compact" ? " compact" : ""}`} data-testid="admin-orders" onClick={() => { if (menuFor) setMenuFor(null); }}>
      <div className="ord-head">
        <ShoppingCart className="w-6 h-6" style={{ color: "var(--ord-lime)" }} />
        <h1 className="ord-title">Orders</h1>
      </div>
      <div className="ord-sub">
        Marketplace orders and swag-store orders in one place — resolve disputes and stuck payouts, and fulfill swag.
        {counts && <b data-testid="orders-needs-action-count"> {needsAction} need{needsAction === 1 ? "s" : ""} action.</b>}
      </div>

      {counts && (counts.needs_action > 0 || counts.disputed > 0) && (
        <div className="ord-attention" data-testid="orders-attention-strip">
          {counts.payout_held > 0 && <button className="ord-att gold" onClick={() => setStatusScope("payout_held")} data-testid="orders-attention-payout_held">{counts.payout_held} payout held</button>}
          {counts.delivery_exception > 0 && <button className="ord-att rust" onClick={() => setStatusScope("delivery_exception")} data-testid="orders-attention-delivery_exception">{counts.delivery_exception} delivery exception{counts.delivery_exception === 1 ? "" : "s"}</button>}
          {counts.disputed > 0 && <button className="ord-att rust" onClick={() => setStatusScope("disputed")} data-testid="orders-attention-disputed">{counts.disputed} disputed</button>}
          {counts.tracking_review > 0 && <button className="ord-att amber" onClick={() => setStatusScope("tracking_review")} data-testid="orders-attention-tracking_review">{counts.tracking_review} tracking to verify</button>}
        </div>
      )}

      <div className="ord-views" data-testid="orders-views">
        {[...views, ...sharedViews].map((v) => {
          const canDel = !v.shared || (user && (user.user_id === v.created_by || ["admin", "super_admin"].includes(user.role)));
          return (
            <span key={v.id} className={`viewchip${v.shared ? " shared" : ""}`} data-testid={`orders-view-${v.id}`}>
              <button onClick={() => applyView(v)} data-testid={`orders-view-apply-${v.id}`} title={v.shared ? `Team view · by @${v.created_by_username || "staff"}` : "Apply this view"}>
                {v.shared && <Users2 />}{v.name}
              </button>
              {canDel && <button className="x" onClick={() => deleteView(v)} data-testid={`orders-view-del-${v.id}`} title="Delete view" aria-label="Delete view">×</button>}
            </span>
          );
        })}
        <button className="viewchip add" onClick={saveView} data-testid="orders-view-save"><Bookmark /> Save view</button>
      </div>

      <div className="ord-filterbar">
        <div className="ord-ffield">
          <label>Date</label>
          <div className="ord-daterange">
            <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="orders-filter-from" />
            <span>–</span>
            <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="orders-filter-to" />
          </div>
        </div>
        <div className="ord-ffield">
          <label>Order ID</label>
          <input className="ord-input" placeholder="ord_ / swo_…" value={fOid} onChange={(e) => setFOid(e.target.value)} data-testid="orders-filter-oid" />
        </div>
        <div className="ord-ffield">
          <label>Customer</label>
          <input className="ord-input" placeholder="Name, @user or email" value={fCust} onChange={(e) => setFCust(e.target.value)} data-testid="orders-filter-customer" />
        </div>
        <div className="ord-ffield">
          <label>Type</label>
          <select className="ord-select" value={fType} onChange={(e) => { setFType(e.target.value); }} data-testid="orders-filter-type">
            <option value="">All types</option>
            <option value="swag">Swag</option>
            <option value="escrow">Marketplace</option>
          </select>
        </div>
        <div className="ord-ffield">
          <label>Status</label>
          <select className="ord-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="orders-filter-status">
            {STATUS_OPTIONS.map((s) => <option key={s.value || "all"} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <button className="ord-clear" onClick={clearFilters} data-testid="orders-clear-filters">Clear filters</button>
      </div>

      <div className="ord-toolbar">
        <button className="ord-export" onClick={exportCsv} data-testid="orders-export"><Download className="w-3.5 h-3.5" /> Export</button>
        {selected.size > 0 && <span className="ord-selcount" data-testid="orders-selected-count">{selected.size} selected</span>}
        <button className="ord-export" onClick={load} title="Refresh" data-testid="orders-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
        <button className="ord-export" onClick={toggleDensity} title="Row density" data-testid="orders-density-toggle"><Rows3 className="w-3.5 h-3.5" /> {density === "compact" ? "Comfortable" : "Compact"}</button>
        <div className="ord-spacer" />
        <div className="ord-pager">
          <span>{sorted.length} order{sorted.length === 1 ? "" : "s"}</span>
          <span>Rows
            <select className="ord-perpage" value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} data-testid="orders-per-page" style={{ marginLeft: 6 }}>
              {[10, 20, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </span>
          {totalPages > 1 && (
            <div className="ord-pages">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={curPage === 1} data-testid="orders-page-prev">‹ Prev</button>
              <button className="cur" data-testid="orders-page-current">{curPage}</button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={curPage === totalPages} data-testid="orders-page-next">Next ›</button>
            </div>
          )}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="ord-bulkbar" data-testid="orders-bulk-bar">
          <span>{selected.size} selected</span>
          <button onClick={exportCsv} data-testid="orders-bulk-export"><Download className="w-3.5 h-3.5" /> Export selected</button>
          <button onClick={bulkMarkShipped} disabled={!selectedSwagShippable.length} data-testid="orders-bulk-ship" title={selectedSwagShippable.length ? "" : "Select swag orders awaiting shipment"}>
            <Truck className="w-3.5 h-3.5" /> Mark shipped{selectedSwagShippable.length ? ` (${selectedSwagShippable.length} swag)` : ""}
          </button>
          <button onClick={() => setSelected(new Set())} data-testid="orders-bulk-clear">Clear selection</button>
        </div>
      )}

      {loadErr ? (
        <LoadFailed reason={loadErr} onRetry={load} testid="admin-orders-load-failed" />
      ) : loading ? (
        <div className="ord-empty" data-testid="admin-orders-loading">Loading orders…</div>
      ) : (
        <div className="ord-tablewrap" data-testid="admin-orders-list">
          <table>
            <thead>
              <tr>
                <th className="chk"><input type="checkbox" checked={allPageSelected} onChange={toggleSelectAll} data-testid="orders-select-all" aria-label="Select all" /></th>
                <th />
                <SortHead label="Date" k="date" />
                <SortHead label="Order ID" k="oid" />
                <SortHead label="Customer" k="customer" />
                <SortHead label="Type" k="type" />
                <SortHead label="Status" k="status" />
                <th style={{ textAlign: "right" }}>Total</th>
                <th style={{ textAlign: "right" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr><td colSpan={9}><div className="ord-empty" style={{ border: "none" }}>No orders match these filters.</div></td></tr>
              )}
              {pageRows.map((o) => {
                const meta = STATUS_META[fstatusOf(o)] || { label: (o.status || "").replace("_", " "), cls: "st-refunded" };
                const isOpen = expanded.has(o.order_id);
                return (
                  <React.Fragment key={o.order_id}>
                    <tr className={`row${isOpen ? " open" : ""}`} data-testid={`admin-order-${o.order_id}`} onClick={() => toggleExpand(o.order_id)}>
                      <td className="chk" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(o.order_id)} onChange={() => toggleSelect(o.order_id)} data-testid={`order-checkbox-${o.order_id}`} aria-label={`Select ${o.order_id}`} />
                      </td>
                      <td className="exp" data-testid={`order-expand-${o.order_id}`}>{isOpen ? "−" : "+"}</td>
                      <td className="date">{fmtDate(o.created_at)}</td>
                      <td className="oid">{o.order_id}</td>
                      <td className="cust"><span className="name">{custName(o)}</span>{custMail(o) && <span className="mail">{custMail(o)}</span>}</td>
                      <td>
                        <span className="typechip" data-testid={`order-type-${o.type}-${o.order_id}`}>
                          {o.type === "swag" ? <><Shirt className="w-3 h-3" /> SWAG</> : "MARKETPLACE"}
                        </span>
                      </td>
                      <td><span className={`statuschip ${meta.cls}`} data-testid={`order-status-${o.order_id}`}><span className="dot" />{meta.label}</span></td>
                      <td className="total">{money(o.amount_cents)}</td>
                      <td className="act" onClick={(e) => e.stopPropagation()}>
                        <button className={`ord-dots${menuFor === o.order_id ? " open" : ""}`} data-testid={`order-menu-btn-${o.order_id}`}
                          onClick={(e) => { e.stopPropagation(); setMenuFor(menuFor === o.order_id ? null : o.order_id); }} aria-label="Order actions">•••</button>
                        {menuFor === o.order_id && (
                          <RowMenu order={o} onClose={() => setMenuFor(null)} expand={() => toggleExpand(o.order_id)}
                            resolve={resolve} refund={(x) => resolve(x, "refund")} simulate={simulateDelivery} openDm={setDmOrder} discuss={discuss} />
                        )}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="detail">
                        <td colSpan={9}>
                          <OrderExpand order={o} busy={busy} resolve={resolve} addressReview={addressReview} simulateDelivery={simulateDelivery} openDm={setDmOrder} onChanged={load} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {bulkShipOpen && (
        <div className="ord-modal-overlay" data-testid="bulk-ship-modal" onClick={() => setBulkShipOpen(false)}>
          <div className="ord-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark {selectedSwagShippable.length} swag order{selectedSwagShippable.length === 1 ? "" : "s"} shipped</h3>
            <p className="hint">Buyers are emailed once shipped. Tracking is optional — leave blank if handled individually.</p>
            <label htmlFor="bulk-carrier">Carrier</label>
            <select id="bulk-carrier" value={bulkCarrier} onChange={(e) => setBulkCarrier(e.target.value)} data-testid="bulk-ship-carrier">
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label htmlFor="bulk-tracking">Tracking number (optional · shared across all)</label>
            <input id="bulk-tracking" type="text" value={bulkTracking} onChange={(e) => setBulkTracking(e.target.value)} placeholder="1Z… (leave blank if none)" data-testid="bulk-ship-tracking" />
            <div className="ord-modal-actions">
              <button className="dbtn ghost" onClick={() => setBulkShipOpen(false)} data-testid="bulk-ship-cancel">Cancel</button>
              <button className="dbtn release" onClick={confirmBulkShip} disabled={bulkBusy} data-testid="bulk-ship-confirm"><Truck className="w-3.5 h-3.5" /> {bulkBusy ? "Shipping…" : "Mark shipped"}</button>
            </div>
          </div>
        </div>
      )}

      {saveViewOpen && (
        <div className="ord-modal-overlay" data-testid="save-view-modal" onClick={() => setSaveViewOpen(false)}>
          <div className="ord-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Save this view</h3>
            <p className="hint">Saves the current filters, sort and rows-per-page.</p>
            <label htmlFor="view-name">View name</label>
            <input id="view-name" type="text" value={saveViewName} onChange={(e) => setSaveViewName(e.target.value)} maxLength={40}
              placeholder="e.g. Payout held, oldest first" autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") confirmSaveView(); }} data-testid="save-view-name" />
            <label className="chk-row" htmlFor="view-share">
              <input id="view-share" type="checkbox" checked={saveViewShare} onChange={(e) => setSaveViewShare(e.target.checked)} data-testid="save-view-share" />
              <span><Users2 className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "-2px", marginRight: 4 }} />Share with the whole team</span>
            </label>
            <div className="ord-modal-actions">
              <button className="dbtn ghost" onClick={() => setSaveViewOpen(false)} data-testid="save-view-cancel">Cancel</button>
              <button className="dbtn release" onClick={confirmSaveView} disabled={!saveViewName.trim()} data-testid="save-view-confirm"><Bookmark className="w-3.5 h-3.5" /> Save view</button>
            </div>
          </div>
        </div>
      )}

      {dmOrder && (
        <AdminDmViewer
          caseType="order"
          caseId={dmOrder.order_id}
          title={`${dmOrder.listing_title || "Order"} · @${dmOrder.buyer_username} ↔ @${dmOrder.seller_username}`}
          open={!!dmOrder}
          onClose={() => setDmOrder(null)}
        />
      )}
    </div>
  );
}
